import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UsersRound, UserPlus, Loader2, Trash2, Power, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { owner: "Propriétaire", admin: "Administrateur", employee: "Employé" };
const PERM_LABELS = {
  view_stats: "Voir les statistiques / finances", view_margins: "Voir les marges",
  create_invoice: "Créer des factures", edit_invoice: "Modifier des factures",
  record_payment: "Enregistrer des paiements", edit_prices: "Modifier les prix",
  manage_stock: "Gérer le stock", manage_clients: "Gérer les clients",
  delete_appointments: "Supprimer des RDV", access_settings: "Accéder aux paramètres",
  export_data: "Exporter les données",
};

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee", phone: "" });
  const [busy, setBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [permUser, setPermUser] = useState(null);
  const [permKeys, setPermKeys] = useState([]);
  const [perms, setPerms] = useState({});

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/team/permission-keys").then((r) => setPermKeys(r.data.keys || [])).catch(() => {}); }, []);

  const openPerms = (u) => { setPermUser(u); setPerms(u.permissions || {}); };
  const savePerms = async () => {
    try { await api.put(`/users/${permUser.id}/permissions`, { permissions: perms }); toast.success("Permissions enregistrées"); setPermUser(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invite = async () => {
    if (!form.name || !form.email) return toast.error("Nom et email requis");
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setInviteLink(data.invite_link || null);
      toast.success("Invitation créée");
      setForm({ name: "", email: "", role: "employee", phone: "" });
      load();
      if (!data.invite_link) setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setRole = async (id, role) => { try { await api.patch(`/users/${id}`, { role }); toast.success("Rôle mis à jour"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleActive = async (u) => { try { await api.patch(`/users/${u.id}`, { active: u.active === false }); toast.success(u.active === false ? "Accès réactivé" : "Accès désactivé"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Utilisateur supprimé"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Équipe" subtitle="Gérez les accès de vos collaborateurs" icon={UsersRound}
        actions={isOwner && <Button onClick={() => { setInviteLink(null); setOpen(true); }} data-testid="btn-open-invite" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><UserPlus className="w-4 h-4 mr-1.5" /> Inviter</Button>} />
      {!isOwner && <p className="text-sm text-slate-500 mb-4">Seul le propriétaire peut inviter ou modifier des utilisateurs.</p>}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="team-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`team-user-${u.id}`}>
            <div className="min-w-0">
              <div className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">{u.name}
                <Badge variant="outline" className={`text-[10px] ${u.role === "owner" ? "text-emerald-600 border-emerald-500/30" : u.role === "admin" ? "text-sky-600 border-sky-500/30" : "text-slate-500 border-slate-300"}`}>{ROLE_LABEL[u.role]}</Badge>
                {u.active === false && <Badge variant="outline" className="text-[10px] text-red-600 border-red-500/30">Désactivé</Badge>}
              </div>
              <div className="text-xs text-slate-500 truncate">{u.email}{u.email_verified === false ? " · email non vérifié" : ""}</div>
            </div>
            {isOwner && u.role !== "owner" && (
              <div className="flex items-center gap-2 shrink-0">
                <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}><SelectTrigger className="h-8 w-32 bg-white border-slate-200 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="admin">Administrateur</SelectItem><SelectItem value="employee">Employé</SelectItem></SelectContent></Select>
                <Button size="sm" variant="outline" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} className="border-slate-200 h-8 px-2"><Power className="w-3.5 h-3.5" /></Button>
                {u.role === "employee" && <Button size="sm" variant="outline" onClick={() => openPerms(u)} data-testid={`perms-${u.id}`} className="border-slate-200 h-8 px-2"><ShieldCheck className="w-3.5 h-3.5" /></Button>}
                <Button size="sm" variant="outline" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="border-red-500/30 bg-red-500/5 text-red-600 h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-slate-500">Aucun utilisateur.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Inviter un collaborateur</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-3" data-testid="invite-link-box">
              <p className="text-sm text-slate-600">Invitation créée. En aperçu, transmettez ce lien pour définir le mot de passe :</p>
              <div className="flex items-center gap-2"><Input readOnly value={inviteLink} data-testid="invite-link" className="bg-slate-50 border-slate-200 text-xs" /><Button size="sm" onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Copié"); }} className="bg-slate-900 text-white"><Copy className="w-4 h-4" /></Button></div>
              <a href={inviteLink} className="text-emerald-600 text-sm hover:underline" data-testid="invite-link-open">Ouvrir le lien</a>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="invite-role" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="admin">Administrateur</SelectItem><SelectItem value="employee">Employé</SelectItem></SelectContent></Select></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Fermer</Button>
            {!inviteLink && <Button onClick={invite} disabled={busy} data-testid="btn-send-invite" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Créer l'invitation</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[90vh] overflow-y-auto" data-testid="perms-dialog">
          <DialogHeader><DialogTitle className="text-slate-900">Permissions — {permUser?.name}</DialogTitle><DialogDescription className="sr-only">Droits d'accès par membre</DialogDescription></DialogHeader>
          <p className="text-xs text-slate-500">Les droits sont contrôlés côté serveur. L'assistant IA respecte aussi « Voir les statistiques / finances ».</p>
          <div className="space-y-2 mt-2">
            {(permKeys.length ? permKeys : Object.keys(PERM_LABELS)).map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`perm-${k}`}>
                <input type="checkbox" checked={!!perms[k]} onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })} className="accent-emerald-500" />
                {PERM_LABELS[k] || k}
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={savePerms} data-testid="perms-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
